import { useEffect, useRef, useState, useCallback } from "react";
import { FaSearch } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

const NarrateSearchButton = () => {
    const searchRef = useRef(null);
    const inputRef = useRef(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        if (searchOpen && inputRef.current) {
            setTimeout(() => {
                inputRef.current.focus();
            }, 100);
        }
    }, [searchOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setSearchOpen(false);
                setSearchQuery("");
            }
        };
        if (searchOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [searchOpen]);

    const handleSearchSubmit = useCallback(() => {
        if (searchQuery.trim()) {
            navigate(`/naray8/search?q=${encodeURIComponent(searchQuery.trim())}`);
            setSearchOpen(false);
            setSearchQuery("");
        }
    }, [searchQuery, navigate]);

    const handleClearSearch = useCallback(() => {
        setSearchQuery("");
        inputRef.current?.focus();
    }, []);

    const handleCloseSearch = useCallback(() => {
        setSearchOpen(false);
        setSearchQuery("");
    }, []);

    const handleRealTimeSearch = useCallback((value) => {
        setSearchQuery(value);
        if (value.trim()) {
            const url = new URL(window.location);
            url.searchParams.set('q', value.trim());
            window.history.replaceState({}, '', url);
        } else {
            const url = new URL(window.location);
            url.searchParams.delete('q');
            window.history.replaceState({}, '', url);
        }
    }, []);

    return (
        <div ref={searchRef} className="relative flex items-center">
            <div
                className={`
                    transition-all duration-300 ease-in-out
                    ${searchOpen 
                        ? "opacity-100 visible" 
                        : "opacity-0 invisible pointer-events-none"
                    }
                    fixed md:absolute
                    left-1/2 -translate-x-1/2
                    w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)]
                    z-50 md:z-auto
                    top-4 md:top-1/2 md:-translate-y-1/2
                    md:right-0 md:left-auto md:translate-x-0
                    md:w-[300px] md:max-w-[300px]
                    lg:w-[350px]
                    md:mr-2
                `}
            >
                <div className="relative w-full">
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => handleRealTimeSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearchSubmit();
                            }
                            if (e.key === 'Escape') {
                                handleCloseSearch();
                            }
                        }}
                        placeholder="Search by title, author, or genre..."
                        className="
                            w-full
                            px-5 py-4 text-base
                            md:px-4 md:py-2.5 md:text-sm
                            bg-white/95 md:bg-white/20
                            backdrop-blur-lg rounded-full
                            text-gray-800 md:text-white
                            placeholder-gray-500 md:placeholder-white/60
                            border-2 md:border
                            border-customGold/50 md:border-white/30
                            focus:outline-none focus:border-customGold focus:ring-2 focus:ring-customGold/50
                            transition-all duration-300
                            shadow-xl md:shadow-none
                        "
                        autoComplete="off"
                    />
                    
                    {searchQuery && (
                        <button
                            onClick={handleClearSearch}
                            className="
                                absolute right-14 md:right-12 
                                top-1/2 transform -translate-y-1/2 
                                text-gray-500 md:text-white/60 
                                hover:text-gray-700 md:hover:text-white
                                transition-colors p-1
                            "
                            type="button"
                        >
                            <IoClose size={20} />
                        </button>
                    )}
                    
                    <button
                        onClick={handleSearchSubmit}
                        className="
                            absolute right-10 md:right-10
                            top-1/2 transform -translate-y-1/2
                            bg-customDark text-white
                            px-4 py-1.5 rounded-full
                            text-sm font-semibold
                            hover:bg-customGold/90
                            transition-colors
                            hidden md:block
                        "
                        type="button"
                    >
                        Search
                    </button>

                    <button
                        onClick={handleSearchSubmit}
                        className="
                            absolute right-3 md:hidden
                            top-1/2 transform -translate-y-1/2
                            bg-customGold text-white
                            w-8 h-8 rounded-full
                            flex items-center justify-center
                            hover:bg-customGold/90
                            transition-colors
                        "
                        type="button"
                    >
                        <FaSearch size={14} />
                    </button>
                </div>
            </div>

            <button
                onClick={() => {
                    if (!searchOpen) {
                        setSearchOpen(true);
                        setSearchQuery("");
                        setTimeout(() => inputRef.current?.focus(), 100);
                    } else if (searchOpen && !searchQuery) {
                        handleCloseSearch();
                    }
                }}
                className={`
                    relative z-50 md:z-auto
                    w-10 h-10 sm:w-10 sm:h-10 md:w-10 md:h-10 
                    bg-white/10 backdrop-blur-lg rounded-full 
                    flex items-center justify-center
                    text-white text-lg sm:text-xl md:text-2xl 
                    hover:bg-white/20 transition-all duration-300 
                    active:scale-95 md:hover:scale-110
                    border border-white/20
                    ${searchOpen ? "bg-customGold/30 border-customGold ring-2 ring-customGold/50" : ""}
                    touch-manipulation
                    shadow-lg md:shadow-none
                `}
                aria-label={searchOpen ? "Close search" : "Open search"}
            >
                {searchOpen ? <IoClose /> : <FaSearch />}
            </button>
        </div>
    );
};

export default NarrateSearchButton;